import { Injectable } from '@angular/core';
import { HttpOptions, OfficeRosterApiService } from '@tangilla/tg-ng-core';
import { Model } from 'survey-core';
import * as Survey from 'survey-core';
import { RequestParameters } from '@tangilla/tg-ng-models';
import { MappedFieldsService } from './mapped-fields.service'
import { firstValueFrom } from 'rxjs';
import { ErrorsService } from './errors.service';

@Injectable({
  providedIn: 'root'
})
export class OfficeRosterUtilsService {
  constructor(
    private officeRosterApiService: OfficeRosterApiService,
    private mappedFieldsService: MappedFieldsService,
    public errorsService: ErrorsService
  ) {
  }

  isLoading = false;
  formTitle = ''
  async loadOfficeRoster(officeNrdsId: any, memberNrdsId: any, isPreview: any,  model: Model) {

    this.isLoading = true;
    const mappedField = this.mappedFieldsService.getMappedFields(model);
    await this.getOfficeRoster(officeNrdsId, memberNrdsId, isPreview, model, mappedField);
    this.isLoading = false;
  }

  async getOfficeRoster(officeNrdsId: any, memberNrdsId: any, isPreview: any, model: Model, mapped: any) {
    if (!officeNrdsId) return;

    try {
      const rosterData = await this.fetchRosterData(officeNrdsId);
      if (this.isEmptyRoster(rosterData, isPreview)) return;

      const roster = await this.buildCompleteRoster(officeNrdsId, rosterData);
      this.populateOfficeRosterQuestion(model, mapped, roster, memberNrdsId);
    } catch (error) {
      this.errorsService.processError(error)
    }
  }

  private async fetchRosterData(officeNrdsId: string): Promise<any> {
    const params = new RequestParameters('status=active', undefined, 100, 0, 'first_name', 'ASC');
    params.setAdditionalFields({ role: 'RPR,RSR,ROR,DPR,DSR,DOR,DMX,RMX' });
    const options: HttpOptions = { params: params.toHttpParams() };
    
    return await firstValueFrom(this.officeRosterApiService.getRoster(officeNrdsId, options));
  }

  private isEmptyRoster(rosterData: any, isPreview: boolean): boolean {
    if (rosterData.total_items === 0) {
      if (isPreview) {        
        alert('This form will not display outside of preview mode');
      } else {
        this.errorsService.exitForm(`${this.formTitle} : Not Available`, `The office has no Team Members.`)
      }
      return true;
    }
    return false;
  }

  private async buildCompleteRoster(officeNrdsId: string, initialRosterData: any): Promise<any[]> {
    let roster = [...initialRosterData.items];
    let offset = 100;
    const limit = 100;

    while (initialRosterData.total_items > limit + offset) {
      const nextRosterData = await this.fetchNextRosterPage(officeNrdsId, offset, limit);
      roster = [...roster, ...nextRosterData.items];
      offset += limit;
    }

    return roster;
  }

  private async fetchNextRosterPage(officeNrdsId: string, offset: number, limit: number): Promise<any> {
    const nextParams = new RequestParameters('status=active', undefined, limit, offset, 'first_name', 'ASC');
    nextParams.setAdditionalFields({ role: 'RPR,RSR,ROR,DPR,DSR,DOR,DMX,RMX' });
    const nextOptions: HttpOptions = { params: nextParams.toHttpParams() };
    
    return await firstValueFrom(this.officeRosterApiService.getRoster(officeNrdsId, nextOptions));
  }

  private populateOfficeRosterQuestion(model: Model, mapped: any, roster: any[], memberNrdsId: string): void {
    if (!mapped.OfficeRoster.question) return;

    const question = model.getQuestionByName(mapped.OfficeRoster.question);
    this.clearQuestionChoices(question);
    this.addTeamMemberChoices(question, roster, memberNrdsId);
  }

  private clearQuestionChoices(question: any): void {
    question['choices'].splice(0, question['choices'].length);
  }

  private addTeamMemberChoices(question: any, roster: any[], memberNrdsId: string): void {
    roster.forEach(member => {
      if (member.member_nrds_id !== memberNrdsId) {
        const teamMemberValue = this.createTeamMemberValue(member);
        const teamMemberDesc = this.createTeamMemberDescription(member);
        const item = new Survey.ItemValue(teamMemberValue, teamMemberDesc);
        question['choices'].push(item);
      }
    });
  }

  private createTeamMemberValue(member: any): string {
    return `${member.member_nrds_id}:${member.full_name}:${member.license_number}`;
  }

  private createTeamMemberDescription(member: any): string {
    return `${member.full_name} (#${member.license_number})`;
  }
    
}
